import type { JobProperty } from "./property";
import type { SigningView } from "./signing";
import type { PaymentAccount } from "./receipts";
import type { CalendarView, MapView } from "./settings";
import type { PricedLine, RetailPriceList, RetailTotals } from "./retail";
import type { AppointmentWeather } from "./weather";
import { z } from "zod";
import type { PermissionKey } from "./permissions";
import type { StageKind } from "./workflows";

/** Shapes exchanged between the web app and the API. */

/** The dashboard's cards, in their default order. */
export const DASHBOARD_CARDS = ["stats", "assistant", "tasks", "agenda", "pastDue", "insurance", "pipeline", "attention", "newLeads", "recent"] as const;
export type DashboardCard = (typeof DASHBOARD_CARDS)[number];
export const DASHBOARD_CARD_LABELS: Record<DashboardCard, string> = {
  stats: "Numbers (open jobs, new leads, need a nudge)",
  assistant: "Fred's suggestions (from your texts)",
  tasks: "My tasks",
  agenda: "Agenda",
  pastDue: "Past due invoices",
  insurance: "Sub insurance to chase",
  pipeline: "Pipeline",
  attention: "Needs attention (stuck jobs)",
  newLeads: "New leads",
  recent: "Recently updated",
};
/** Someone's dashboard: the cards in their order, and the ones they hid. */
export const dashboardLayoutSchema = z.object({
  order: z.array(z.enum(DASHBOARD_CARDS)).max(DASHBOARD_CARDS.length),
  hidden: z.array(z.enum(DASHBOARD_CARDS)).max(DASHBOARD_CARDS.length),
});
export type DashboardLayout = z.infer<typeof dashboardLayoutSchema>;

export interface MeResponse {
  user: { id: string; email: string; firstName: string; lastName: string; phone: string; color: string; roleIds: string[]; rcExtensionId: string | null };
  /** They can send addresses to the Rivian (and it's set up on this server). */
  rivian: boolean;
  company: { name: string; timezone: string };
  calendarViews: CalendarView[];
  permissions: PermissionKey[];
  theme: { primaryColor: string; sidebarColor: string; logoUrl: string | null };
  lists: { leadSources: string[]; contactRoles: string[]; fileCategories: string[]; photoTags: string[]; appointmentTypes: string[]; expenseCategories: string[]; permitCities: string[] };
  contactTypes: ContactTypeItem[];
  /** Accounts paid expenses can come out of (Settings → QuickBooks). */
  paymentAccounts: (PaymentAccount & { isDefault: boolean })[];
  /** "staging" shows a banner; "production" doesn't. */
  stage: string;
  sessionExpiresAt: string;
  /** Their dashboard layout; null: the default. */
  dashboard: DashboardLayout | null;
}

// ── Users and roles ──────────────────────────────────────────────────────

export interface UserListItem {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  color: string;
  status: "invited" | "active" | "deactivated";
  roleIds: string[];
  lastSignInAt: string | null;
  rcExtensionId: string | null;
  /** Default commission, % of a job's net profit after overhead (40 = 40%); null: none set. */
  commissionPercent: number | null;
  /** Who they report to (for team notifications). */
  managerId: string | null;
}

export interface UserDetail extends UserListItem {
  overrides: { permission: string; effect: "grant" | "revoke" }[];
  effectivePermissions: PermissionKey[];
}

/** Short form for pickers (sales rep) and names on records. */
export interface UserRef {
  id: string;
  name: string;
  color: string;
  active: boolean;
}

export interface RoleItem {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  userCount: number;
}

export interface SessionItem {
  id: string;
  userId: string;
  userName: string;
  createdAt: string;
  lastSeenAt: string;
  ip: string | null;
  device: string;
  current: boolean;
}

export interface AuditEntry {
  id: string;
  occurredAt: string;
  actorName: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  details: Record<string, unknown>;
  ip: string | null;
}

export const nameSchema = z.string().trim().min(1).max(80);
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const inviteUserSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  firstName: nameSchema,
  lastName: nameSchema,
  phone: z.string().trim().max(30).optional().nullable(),
  color: hexColor.optional(),
  roleIds: z.array(z.string().uuid()).min(1, "Pick at least one role"),
});

/** My profile: what people change about themselves. */
export const updateMeSchema = z.object({
  firstName: nameSchema.optional(),
  lastName: nameSchema.optional(),
  phone: z.string().trim().max(30).optional(),
  color: hexColor.optional(),
});

export const updateUserSchema = z.object({
  firstName: nameSchema.optional(),
  lastName: nameSchema.optional(),
  phone: z.string().trim().max(30).nullable().optional(),
  color: hexColor.optional(),
  /** Their RingCentral extension (one of Settings → Texts and calls → Extensions). */
  rcExtensionId: z.string().regex(/^\d{1,20}$/).nullable().optional(),
  /** % of a job's net profit after overhead, to two decimals. */
  commissionPercent: z.number().min(0).max(100).nullable().optional(),
  managerId: z.string().uuid().nullable().optional(),
  roleIds: z.array(z.string().uuid()).optional(),
  overrides: z.array(z.object({ permission: z.string(), effect: z.enum(["grant", "revoke"]) })).optional(),
});

export const roleInputSchema = z.object({
  name: nameSchema,
  description: z.string().trim().max(300).default(""),
  permissions: z.array(z.string()),
});

// ── Workflows ────────────────────────────────────────────────────────────

export interface StageItem {
  id: string;
  name: string;
  kind: StageKind;
  sort: number;
  jobCount: number;
}

export interface WorkflowItem {
  id: string;
  name: string;
  insurance: boolean;
  qbCreateStageId: string | null;
  /** The job goes to QuickBooks as soon as it's made (else at the stage above, if any). */
  qbCreateOnNew: boolean;
  /** Jobs move here (forward only) when a proposal is sent, and when one is signed. */
  proposalSentStageId: string | null;
  proposalSignedStageId: string | null;
  sort: number;
  archived: boolean;
  stages: StageItem[];
}

export const workflowInputSchema = z.object({
  name: nameSchema,
  insurance: z.boolean().default(false),
  /** Index into `stages` (stages may be new and have no ID yet). */
  qbCreateStageIndex: z.number().int().min(0).nullable().default(null),
  /** Create the QuickBooks job as soon as the job is made (the stage is then ignored). */
  qbCreateOnNew: z.boolean().default(false),
  /** Indexes into `stages`, like qbCreateStageIndex: where jobs move when a proposal is sent, and signed. */
  proposalSentStageIndex: z.number().int().min(0).nullable().default(null),
  proposalSignedStageIndex: z.number().int().min(0).nullable().default(null),
  stages: z
    .array(
      z.object({
        id: z.string().uuid().optional(),
        name: nameSchema,
        kind: z.enum(["open", "closed", "lost"]),
      }),
    )
    .min(2, "A workflow needs at least two stages")
    .max(40),
});

// ── Contacts ─────────────────────────────────────────────────────────────

export interface Address {
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

const addressField = {
  street: z.string().trim().max(200),
  street2: z.string().trim().max(200),
  city: z.string().trim().max(100),
  state: z.string().trim().max(30),
  zip: z.string().trim().max(20),
};
/** For new records: anything left out is empty (state defaults to OK). */
const addressShape = {
  street: addressField.street.default(""),
  street2: addressField.street2.default(""),
  city: addressField.city.default(""),
  state: addressField.state.default("OK"),
  zip: addressField.zip.default(""),
};
/** For updates: only what's sent changes. */
const addressPatch = {
  street: addressField.street.optional(),
  street2: addressField.street2.optional(),
  city: addressField.city.optional(),
  state: addressField.state.optional(),
  zip: addressField.zip.optional(),
};

/**
 * What a contact type means to the rest of the system. Customers can have jobs and become
 * QuickBooks customers; subcontractors and vendors become QuickBooks vendors (subcontractors
 * also get the insurance and W-9 checks); other contacts stay in the CRM.
 */
export const CONTACT_CATEGORIES = ["customer", "subcontractor", "vendor", "other"] as const;
export type ContactCategory = (typeof CONTACT_CATEGORIES)[number];

export const CONTACT_CATEGORY_LABELS: Record<ContactCategory, { label: string; quickbooks: string }> = {
  customer: { label: "Customer", quickbooks: "QuickBooks customer" },
  subcontractor: { label: "Subcontractor", quickbooks: "QuickBooks vendor" },
  vendor: { label: "Vendor", quickbooks: "QuickBooks vendor" },
  other: { label: "Other", quickbooks: "Not sent to QuickBooks" },
};

export interface ContactTypeItem {
  id: string;
  name: string;
  category: ContactCategory;
  sort: number;
  /** Contacts of this type, including ones in the recycle bin. */
  contactCount: number;
}

export const contactTypeInputSchema = z.object({
  name: z.string().trim().min(1, "Enter a name").max(60),
  category: z.enum(CONTACT_CATEGORIES),
});

export const contactTypeOrderSchema = z.object({ ids: z.array(z.string().uuid()).max(100) });

export interface ContactListItem extends Address {
  id: string;
  typeId: string;
  typeName: string;
  category: ContactCategory;
  kind: "residential" | "commercial";
  displayName: string;
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  jobCount: number;
  openJobCount: number;
  updatedAt: string;
}

export interface RelatedContact {
  contactId: string;
  role: string;
  displayName: string;
  typeName: string;
  company: string;
  email: string;
  phone: string;
}

export interface ContactDetail extends ContactListItem {
  phone2: string;
  /** A vendor's or sub's payment terms ("Net 30"). */
  paymentTerms: string;
  notes: string;
  qbCustomerId: string | null;
  qbVendorId: string | null;
  /** Other names this vendor is printed as on receipts, learned when someone picks it. */
  vendorAliases: string[];
  /** A sub or vendor no longer used: off the insurance list, reminders, and new work orders. */
  inactive: boolean;
  createdAt: string;
  deletedAt: string | null;
  /** Their jobs, when they're a customer. */
  jobs: JobListItem[];
  /** Contacts linked to this one (spouse, property manager...). */
  contacts: RelatedContact[];
  /** Where this contact is listed as someone else's related contact. */
  linkedFrom: { id: string; displayName: string; role: string }[];
  /** Jobs this contact is on as a related contact (adjuster on a claim...). */
  onJobs: { id: string; number: number; label: string; role: string }[];
  /** Jobs this contact referred to us. */
  referrals: { id: string; number: number; label: string }[];
}

const email = z.union([z.literal(""), z.string().trim().toLowerCase().email().max(254)]);

export const contactInputSchema = z
  .object({
    typeId: z.string().uuid(),
    kind: z.enum(["residential", "commercial"]).default("residential"),
    firstName: z.string().trim().max(80).default(""),
    lastName: z.string().trim().max(80).default(""),
    company: z.string().trim().max(120).default(""),
    email: email.default(""),
    phone: z.string().trim().max(30).default(""),
    phone2: z.string().trim().max(30).default(""),
    paymentTerms: z.string().trim().max(60).default(""),
    ...addressShape,
    notes: z.string().trim().max(4000).default(""),
  })
  .refine((c) => (c.kind === "commercial" ? c.company : c.firstName || c.lastName || c.company), {
    message: "Enter a name (or a company name for a business)",
    path: ["lastName"],
  });

export const contactUpdateSchema = z.object({
  typeId: z.string().uuid().optional(),
  kind: z.enum(["residential", "commercial"]).optional(),
  firstName: z.string().trim().max(80).optional(),
  lastName: z.string().trim().max(80).optional(),
  company: z.string().trim().max(120).optional(),
  email: email.optional(),
  phone: z.string().trim().max(30).optional(),
  phone2: z.string().trim().max(30).optional(),
  paymentTerms: z.string().trim().max(60).optional(),
  ...addressPatch,
  notes: z.string().trim().max(4000).optional(),
  /** A sub or vendor no longer used. */
  inactive: z.boolean().optional(),
  vendorAliases: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
});

export const linkContactSchema = z.object({
  contactId: z.string().uuid(),
  role: z.string().trim().max(60).default(""),
});

// ── Jobs ─────────────────────────────────────────────────────────────────

export interface JobListItem extends Address {
  id: string;
  number: number;
  title: string;
  customerId: string;
  customerName: string;
  workflowId: string;
  workflowName: string;
  stageId: string;
  stageName: string;
  stageKind: StageKind;
  stageChangedAt: string;
  salesRep: UserRef | null;
  leadSource: string;
  updatedAt: string;
  createdAt: string;
  claimNumber: string;
  insuranceCarrier: string;
  /** Only when asked for (a money column or sort) and they can see money. */
  contractCents?: number;
  balanceCents?: number;
}

export interface JobDetail extends JobListItem {
  /** Reps besides the commissioned rep (salesRep): the job is theirs too, without commission. */
  otherReps: UserRef[];
  description: string;
  insurance: boolean;
  insuranceCarrier: string;
  claimNumber: string;
  policyNumber: string;
  dateOfLoss: string | null;
  deductibleCents: number | null;
  qbSubCustomerId: string | null;
  /** QuickBooks name: the linked one, or what it will be when it's created. */
  qbName: string;
  qbCreateStageName: string | null;
  /** Its workflow sends new jobs to QuickBooks as soon as they're made. */
  qbCreateOnNew: boolean;
  customer: { id: string; displayName: string; phone: string; phone2: string; email: string; lastName: string; kind: "residential" | "commercial" } & Address;
  contacts: RelatedContact[];
  stages: { id: string; name: string; kind: StageKind }[];
  /** Null when the user can't see money. Filled in by later phases (proposals, invoices, payments). */
  money: { contractCents: number; paidCents: number; creditCents: number; balanceCents: number } | null;
  /** Who sent us this job. */
  referredBy: { id: string; name: string } | null;
  /** County property records, looked up once when the job was made (null: never looked up). */
  property: JobProperty | null;
  /** The picture at the top: a photo tagged "Street View" if there is one, else Google Street View (if set up). */
  streetView: { photoUrl: string | null; photoId: string | null; googleUrl: string | null };
  deletedAt: string | null;
}

const jobFieldsShape = {
  title: z.string().trim().max(120).default(""),
  ...addressShape,
  leadSource: z.string().trim().max(60).default(""),
  referredById: z.string().uuid().nullable().default(null),
  salesRepId: z.string().uuid().nullable().default(null),
  /** Other reps (not the commissioned one). */
  otherRepIds: z.array(z.string().uuid()).max(10).default([]),
  description: z.string().trim().max(8000).default(""),
  insuranceCarrier: z.string().trim().max(120).default(""),
  claimNumber: z.string().trim().max(60).default(""),
  policyNumber: z.string().trim().max(60).default(""),
  dateOfLoss: z.string().date().nullable().default(null),
  deductibleCents: z.number().int().min(0).max(100_000_000).nullable().default(null),
};

export const jobInputSchema = z.object({
  customerId: z.string().uuid(),
  workflowId: z.string().uuid(),
  /** Defaults to the workflow's first stage. */
  stageId: z.string().uuid().optional(),
  ...jobFieldsShape,
  /** Where the address is, when the form knows (picked suggestion, "use my location"): for county records. */
  position: z.object({ lat: z.number().min(-90).max(90), lon: z.number().min(-180).max(180) }).optional(),
});

/** A job for someone who isn't a contact yet: the customer and the job, made together. */
export const jobWithCustomerSchema = z.object({
  customer: contactInputSchema,
  job: jobInputSchema.omit({ customerId: true }),
});

export const jobUpdateSchema = z.object({
  title: z.string().trim().max(120).optional(),
  ...addressPatch,
  leadSource: z.string().trim().max(60).optional(),
  referredById: z.string().uuid().nullable().optional(),
  salesRepId: z.string().uuid().nullable().optional(),
  /** Other reps (not the commissioned one): the whole list. */
  otherRepIds: z.array(z.string().uuid()).max(10).optional(),
  description: z.string().trim().max(8000).optional(),
  insuranceCarrier: z.string().trim().max(120).optional(),
  claimNumber: z.string().trim().max(60).optional(),
  policyNumber: z.string().trim().max(60).optional(),
  dateOfLoss: z.string().date().nullable().optional(),
  deductibleCents: z.number().int().min(0).max(100_000_000).nullable().optional(),
  /** Changing the workflow needs a stage in the new workflow. */
  workflowId: z.string().uuid().optional(),
  stageId: z.string().uuid().optional(),
});

export const jobStageSchema = z.object({ stageId: z.string().uuid() });

// ── Notes and history ────────────────────────────────────────────────────

export interface NoteItem {
  id: string;
  body: string;
  pinned: boolean;
  jobId: string | null;
  jobNumber: number | null;
  author: UserRef | null;
  createdAt: string;
  editedAt: string | null;
  mine: boolean;
}

export const noteInputSchema = z.object({ body: z.string().trim().min(1, "Write something first").max(10_000) });
export const noteUpdateSchema = z.object({ body: z.string().trim().min(1).max(10_000).optional(), pinned: z.boolean().optional() });

export interface HistoryEntry {
  id: string;
  occurredAt: string;
  actorName: string | null;
  action: string;
  summary: string;
  details: Record<string, unknown>;
}

// ── Files and photos ─────────────────────────────────────────────────────

/** Largest file the browser may upload (S3 enforces it through the signed upload). */
export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

export interface FileItem {
  id: string;
  jobId: string | null;
  contactId: string | null;
  category: string;
  name: string;
  contentType: string;
  sizeBytes: number;
  isPhoto: boolean;
  width: number | null;
  height: number | null;
  takenAt: string | null;
  latitude: number | null;
  longitude: number | null;
  tags: string[];
  caption: string;
  markup: MarkupShape[];
  uploadedBy: UserRef | null;
  createdAt: string;
  mine: boolean;
  /** Signed links, good for an hour. Thumbnail and medium are photos only (the original if there are none). */
  urls: { original: string; download: string; thumb: string | null; medium: string | null };
}

export const uploadRequestSchema = z.object({
  files: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(255),
        contentType: z.string().trim().min(1).max(120),
        size: z.number().int().min(1).max(MAX_UPLOAD_BYTES),
        /** Ask for upload slots for a thumbnail and a medium copy (photos). */
        variants: z.boolean().default(false),
      }),
    )
    .min(1)
    .max(50),
});

export interface UploadSlot {
  url: string;
  key: string;
  headers: Record<string, string>;
}

export interface UploadSlots {
  original: UploadSlot;
  thumb: UploadSlot | null;
  medium: UploadSlot | null;
}

export const fileCreateSchema = z.object({
  key: z.string().min(1).max(500),
  name: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(120),
  size: z.number().int().min(1).max(MAX_UPLOAD_BYTES),
  category: z.string().trim().max(60).default(""),
  isPhoto: z.boolean().default(false),
  thumbKey: z.string().max(500).nullable().default(null),
  mediumKey: z.string().max(500).nullable().default(null),
  width: z.number().int().min(1).max(100_000).nullable().default(null),
  height: z.number().int().min(1).max(100_000).nullable().default(null),
  takenAt: z.string().datetime({ offset: true }).nullable().default(null),
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  caption: z.string().trim().max(500).default(""),
});

/**
 * A drawing over a photo (the photo itself is never changed). Points are fractions of the
 * image's width and height (0–1), so the markup fits any size of the photo.
 */
export interface MarkupShape {
  kind: "arrow" | "circle" | "box" | "text";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  text?: string;
}
const unit = z.number().min(0).max(1);
export const markupShapeSchema = z.object({ kind: z.enum(["arrow", "circle", "box", "text"]), x1: unit, y1: unit, x2: unit, y2: unit, color: z.string().regex(/^#[0-9a-fA-F]{6}$/), text: z.string().trim().max(80).optional() });
export const MARKUP_COLORS = ["#E53935", "#FDD835", "#FFFFFF", "#1E88E5"];

export const fileUpdateSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  category: z.string().trim().max(60).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  caption: z.string().trim().max(500).optional(),
  markup: z.array(markupShapeSchema).max(50).optional(),
});


export const photoReportSchema = z.object({
  title: z.string().trim().max(120).default("Photo report"),
  /** Only photos with one of these tags (empty: all photos). */
  tags: z.array(z.string().max(40)).max(20).default([]),
  /** Or exactly these photos, in this order. */
  fileIds: z.array(z.string().uuid()).max(300).default([]),
  captions: z.boolean().default(true),
});

// ── Tasks and appointments ───────────────────────────────────────────────

export const TASK_PRIORITIES = ["high", "medium", "low"] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/** What a task or appointment is about: a job (and its customer) or a contact. */
export interface LinkedRecord {
  jobId: string | null;
  jobNumber: number | null;
  contactId: string | null;
  contactName: string | null;
}

export interface TaskItem extends LinkedRecord {
  id: string;
  title: string;
  notes: string;
  priority: TaskPriority;
  dueAt: string | null;
  /** A push notification to the assignee at this time. */
  remindAt: string | null;
  assignee: UserRef;
  /** Also assigned: on their lists and reminders too; anyone can finish it. */
  alsoAssigned: UserRef[];
  createdBy: UserRef | null;
  createdAt: string;
  completedAt: string | null;
  completedBy: UserRef | null;
}

export const taskInputSchema = z.object({
  title: z.string().trim().min(1, "Say what needs doing").max(200),
  notes: z.string().trim().max(4000).default(""),
  priority: z.enum(TASK_PRIORITIES).default("medium"),
  dueAt: z.string().datetime({ offset: true }).nullable().default(null),
  remindAt: z.string().datetime({ offset: true }).nullable().default(null),
  /** Defaults to the person adding it. */
  assigneeId: z.string().uuid().optional(),
  /** Others also assigned. */
  alsoAssigneeIds: z.array(z.string().uuid()).max(10).default([]),
  jobId: z.string().uuid().nullable().default(null),
  contactId: z.string().uuid().nullable().default(null),
});

export const taskUpdateSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  notes: z.string().trim().max(4000).optional(),
  priority: z.enum(TASK_PRIORITIES).optional(),
  dueAt: z.string().datetime({ offset: true }).nullable().optional(),
  remindAt: z.string().datetime({ offset: true }).nullable().optional(),
  assigneeId: z.string().uuid().optional(),
  /** The whole list of others also assigned. */
  alsoAssigneeIds: z.array(z.string().uuid()).max(10).optional(),
  done: z.boolean().optional(),
});

export interface AppointmentItem extends LinkedRecord {
  id: string;
  title: string;
  type: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  location: string;
  notes: string;
  attendees: (UserRef & { googleSynced: boolean; googleError: string | null })[];
  createdBy: UserRef | null;
  /** The last forecast check, for scheduled work within about 6 days. */
  weather: AppointmentWeather | null;
  /** Its own alert times (minutes before); null: its type's, or the company's. */
  alerts: number[] | null;
  /** Busy (the usual) or free: free stays on calendars but never blocks anyone's time. */
  busy: boolean;
}

const appointmentAlerts = z.array(z.number().int().min(0).max(40320)).max(5).nullable();
const appointmentFields = {
  title: z.string().trim().min(1, "Give it a title").max(200),
  type: z.string().trim().max(60).default(""),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }),
  allDay: z.boolean().default(false),
  location: z.string().trim().max(300).default(""),
  notes: z.string().trim().max(4000).default(""),
  attendeeIds: z.array(z.string().uuid()).min(1, "Pick who's going").max(30),
  alerts: appointmentAlerts.default(null),
  busy: z.boolean().default(true),
};

export const appointmentInputSchema = z
  .object({ ...appointmentFields, jobId: z.string().uuid().nullable().default(null), contactId: z.string().uuid().nullable().default(null) })
  .refine((a) => new Date(a.endsAt) >= new Date(a.startsAt), { message: "It has to end after it starts", path: ["endsAt"] });

export const appointmentUpdateSchema = z.object({
  title: appointmentFields.title.optional(),
  type: z.string().trim().max(60).optional(),
  startsAt: appointmentFields.startsAt.optional(),
  endsAt: appointmentFields.endsAt.optional(),
  allDay: z.boolean().optional(),
  location: z.string().trim().max(300).optional(),
  notes: z.string().trim().max(4000).optional(),
  attendeeIds: appointmentFields.attendeeIds.optional(),
  alerts: appointmentAlerts.optional(),
  busy: z.boolean().optional(),
});

// ── Proposals ────────────────────────────────────────────────────────────

export type ProposalStatus = "reading" | "read_failed" | "draft" | "sent" | "viewed" | "signed" | "declined" | "void";

export interface ProposalListItem {
  id: string;
  jobId: string;
  /** Insurance (from the carrier's estimate), retail (a priced roof), or custom (remodels, repairs, commercial, anything else). */
  kind: "insurance" | "retail" | "custom";
  /** "Insurance proposal", "Roof replacement", ... */
  title: string;
  status: ProposalStatus;
  contractCents: number | null;
  outOfPocketCents: number | null;
  signerName: string | null;
  sentAt: string | null;
  signedAt: string | null;
  validThrough: string | null;
  createdAt: string;
  createdBy: UserRef | null;
}

export interface ProposalDetail extends ProposalListItem {
  /** EstimateExtraction, ReconcileCheck[], and ProposalFormState from the proposals module (typed loosely here). */
  extraction: unknown;
  checks: { name: string; ok: boolean; detail: string }[] | null;
  readPasses: number | null;
  readError: string | null;
  form: unknown;
  chat: { role: "user" | "assistant"; text: string }[];
  estimateFile: { id: string; name: string; url: string } | null;
  pdfUrl: string | null;
  signedPdfUrl: string | null;
  /** The customer's link, shown to the rep right after sending (it isn't stored, so it can't be shown again later). */
  customerUrl?: string;
  customer: ProposalCustomerInfo;
  rep: { name: string; phone: string; email: string };
  job: { id: string; number: number; workflowName: string };
  aiAvailable: boolean;
  /** Retail only: the price list it's priced from, and the lines and totals as they stand. */
  retail?: { priceList: RetailPriceList; lines: PricedLine[]; totals: RetailTotals; problems: string[] };
  /** Custom only: the minimum margin, and its revisions (this one revises `revisionOf`; `revisedBy` revises this one). */
  custom?: { minMarginPercent: number; revision: number; revisionOf: string | null; revisedBy: string | null; signerTitle: string | null; signerCompany: string | null; poNumber: string | null; picked: string[] | null; aiBusy: boolean };
}

/** The job's customer as the proposal sees it. */
export interface ProposalCustomerInfo {
  name: string;
  firstName: string;
  address: string;
  cityStateZip: string;
  phone: string;
  email: string;
}

export const proposalCreateSchema = z.object({ estimateFileId: z.string().uuid() });
export const retailProposalCreateSchema = z.object({ kind: z.literal("retail"), variant: z.enum(["retail_roof", "remodel", "repairs", "commercial"]) });
/** A custom proposal; residential or commercial from the customer record unless given. */
export const customProposalCreateSchema = z.object({ kind: z.literal("custom"), market: z.enum(["residential", "commercial"]).optional(), title: z.string().trim().max(120).optional() });
export const proposalChatSchema = z.object({ message: z.string().trim().min(1).max(2000) });
export const proposalSendSchema = z.object({
  signerName: z.string().trim().min(1, "Who's signing?").max(160),
  signerEmail: z.union([z.literal(""), z.string().trim().toLowerCase().email().max(254)]).default(""),
});

/** What the customer sees on their proposal link. */
export interface PublicProposal {
  kind: "insurance" | "retail" | "custom";
  title: string;
  status: ProposalStatus;
  company: { name: string; phone: string; email: string; logoUrl: string | null; primaryColor: string };
  customerName: string;
  address: string;
  contractCents: number;
  outOfPocketCents: number;
  validThrough: string | null;
  expired: boolean;
  pdfUrl: string;
  signedPdfUrl: string | null;
  signedAt: string | null;
  signatureName: string | null;
  repName: string;
  /** Lines the customer fills in when signing ("Shingle color", "Drip edge / metal color"). */
  selections: string[];
  /** Custom: the options to pick from (the contract follows), and whether to ask for title, company, and PO. */
  custom?: {
    commercial: boolean;
    revision: number;
    core: { sectionsCents: number; allowancesCents: number; discountCents: number; taxPercentMilli: number };
    options: { id: string; kind: "add" | "deduct"; label: string; description: string; amountCents: number }[];
    /** Once signed: what was picked. */
    picked: string[] | null;
    signerTitle: string | null;
    signerCompany: string | null;
    poNumber: string | null;
  };
  /** Guided signing (sent since it started): the spots to tap. The document is at /api/public/proposals/:token/document.pdf. */
  signing?: SigningView | null;
}

export const proposalSignSchema = z.object({
  name: z.string().trim().min(2, "Type your full name").max(160),
  initials: z.string().trim().min(1, "Type your initials").max(8),
  /** PNG data URL of the drawn signature. */
  signature: z.string().max(400_000).regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, "Draw your signature"),
  selections: z.array(z.string().trim().max(200)).max(20).default([]),
  comments: z.string().trim().max(2000).default(""),
  /** Custom proposals: the options picked, and a commercial signer's title, company, and PO number. */
  options: z.array(z.string().max(40)).max(40).default([]),
  signerTitle: z.string().trim().max(120).default(""),
  signerCompany: z.string().trim().max(200).default(""),
  poNumber: z.string().trim().max(60).default(""),
  /** Agreed to sign electronically (ESIGN Act consent). */
  consent: z.literal(true, { message: "Check the box to sign electronically" }),
});

export const proposalDeclineSchema = z.object({ reason: z.string().trim().max(2000).default("") });

// ── Search, dashboard, recycle bin ───────────────────────────────────────

export interface SearchResults {
  contacts: { id: string; displayName: string; typeName: string; company: string; phone: string; city: string }[];
  jobs: { id: string; number: number; label: string; stageName: string; address: string }[];
}

export interface DashboardResponse {
  pipeline: { workflowId: string; workflowName: string; stages: { id: string; name: string; kind: StageKind; count: number }[] }[];
  recentJobs: JobListItem[];
  newLeads: JobListItem[];
  /** Open jobs that haven't changed stage in 14+ days. */
  stuckJobs: JobListItem[];
  /** How many in all (the lists show the first 10). */
  newLeadCount: number;
  stuckCount: number;
}

export interface RecycledItem {
  type: "contact" | "job" | "file" | "proposal";
  id: string;
  label: string;
  deletedAt: string;
  deletedBy: string | null;
}

/** Website lead form (public). */
export const publicLeadSchema = z.object({
  firstName: z.string().trim().max(80).default(""),
  lastName: z.string().trim().max(80).default(""),
  name: z.string().trim().max(160).default(""),
  email: z.union([z.literal(""), z.string().trim().toLowerCase().email().max(254)]).default(""),
  phone: z.string().trim().max(30).default(""),
  street: z.string().trim().max(200).default(""),
  city: z.string().trim().max(100).default(""),
  state: z.string().trim().max(30).default("OK"),
  zip: z.string().trim().max(20).default(""),
  message: z.string().trim().max(4000).default(""),
  /** Hidden honeypot field: real visitors leave it empty. */
  website: z.string().max(500).default(""),
});

/**
 * The browsers' push services. Only these are accepted as subscription endpoints, so the server
 * never posts anywhere else (a made-up endpoint could otherwise point it at internal addresses).
 */
export function isPushServiceUrl(endpoint: string) {
  let u: URL;
  try {
    u = new URL(endpoint);
  } catch {
    return false;
  }
  if (u.protocol !== "https:" || u.port || u.username || u.password) return false;
  const h = u.hostname.toLowerCase();
  return h === "fcm.googleapis.com" || h === "android.googleapis.com" || h === "web.push.apple.com" || h.endsWith(".push.services.mozilla.com") || h.endsWith(".notify.windows.com");
}

/** A browser's push subscription (PushSubscription.toJSON()). */
export const pushSubscribeSchema = z.object({
  endpoint: z.string().max(1000).refine(isPushServiceUrl, "That isn't a browser push service address."),
  keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }),
});

/** Kinds of push notification; each person turns them on or off (spec §4–5: per-user opt-in). */
export const NOTIFY_KINDS = ["assigned", "reminders", "weather", "daily", "leads", "proposal_opened", "proposal_signed", "co_signed", "payment", "customer_reply", "job_assigned", "job_moved", "appointments", "appointment_reminders", "sub_documents"] as const;
export type NotifyKind = (typeof NOTIFY_KINDS)[number];
/** job: about something on a job (sent to its reps, and to managers for their team's jobs). */
export const NOTIFY_KIND_INFO: Record<NotifyKind, { label: string; description: string; default: boolean; job: boolean }> = {
  assigned: { label: "Tasks given to me", description: "When someone adds a task for you.", default: true, job: false },
  reminders: { label: "Task reminders", description: "At the reminder time set on a task, or before it's due when it has none.", default: true, job: false },
  weather: { label: "Weather on my scheduled work", description: "When rain or wind is forecast for an install or other scheduled work you're on.", default: false, job: false },
  daily: { label: "Morning reminders", description: "A summary when the daily run adds reminders for you, and on Mondays your past-due invoices.", default: false, job: false },
  leads: { label: "New website leads", description: "When someone fills in the form on the website.", default: false, job: false },
  proposal_opened: { label: "Customer opened a proposal", description: "The first time the customer opens it.", default: true, job: true },
  proposal_signed: { label: "Proposal signed or declined", description: "When the customer signs or declines.", default: true, job: true },
  co_signed: { label: "Change order signed", description: "When the customer signs a change order.", default: true, job: true },
  payment: { label: "Payment received", description: "A card or bank payment through the pay link, or a payment someone records.", default: true, job: true },
  customer_reply: { label: "Customer replied", description: "A text or email from the job's customer.", default: true, job: true },
  job_assigned: { label: "Job given to me", description: "When you're made the job's rep or added as another rep.", default: true, job: false },
  job_moved: { label: "Job moved to another stage", description: "When someone else moves the job, or it moves on its own (proposal sent or signed).", default: false, job: true },
  appointments: { label: "Appointments for me", description: "When someone else puts you on an appointment, changes its day, time, or place, or cancels it.", default: true, job: true },
  sub_documents: { label: "Sub documents", description: "When a sub uploads a W-9 or insurance: what Fred updated on their file, or what needs your look. Admins only.", default: true, job: false },
  appointment_reminders: { label: "Appointment reminders", description: "Before your appointments, at their alert times. Off to start if you've connected Google Calendar (its alerts remind you already).", default: true, job: false },
};
export const NOTIFY_CHANNELS = ["push", "text", "email"] as const;
export type NotifyChannel = (typeof NOTIFY_CHANNELS)[number];
export const NOTIFY_CHANNEL_LABELS: Record<NotifyChannel, string> = { push: "Push", text: "Text", email: "Email" };
export type ChannelSet = Partial<Record<NotifyChannel, boolean>>;
/** mine: your own (and your jobs); others: your team's jobs, or everyone's (with the permission). */
export type NotifySection = "mine" | "others";

const channelSetSchema = z.object({ push: z.boolean().optional(), text: z.boolean().optional(), email: z.boolean().optional() });
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const notifyPrefsSchema = z.object({
  mine: z.partialRecord(z.enum(NOTIFY_KINDS), channelSetSchema).default({}),
  others: z.partialRecord(z.enum(NOTIFY_KINDS), channelSetSchema).default({}),
  /** Whose jobs "others" covers: the people who report to you (and theirs), or everyone's. */
  othersScope: z.enum(["team", "everyone"]).default("team"),
  /** Texts wait until quietEnd between these times (company time zone). */
  quietStart: hhmm.default("21:00"),
  quietEnd: hhmm.default("07:00"),
  /** Set by an admin: the person can't change their own. */
  locked: z.boolean().default(false),
});
export type NotifyPrefs = z.infer<typeof notifyPrefsSchema>;

/** Stored prefs (including the old shape: { kind: boolean } for push) as the full shape. */
export function normalizeNotifyPrefs(raw: unknown): NotifyPrefs {
  const r = (raw ?? {}) as Record<string, unknown>;
  const parsed = notifyPrefsSchema.safeParse({ mine: r.mine ?? {}, others: r.others ?? {}, othersScope: r.othersScope, quietStart: r.quietStart, quietEnd: r.quietEnd, locked: r.locked });
  const p: NotifyPrefs = parsed.success ? parsed.data : notifyPrefsSchema.parse({});
  for (const k of NOTIFY_KINDS) {
    if (typeof r[k] === "boolean" && p.mine[k]?.push === undefined) p.mine[k] = { ...p.mine[k], push: r[k] as boolean };
  }
  return p;
}

/** Whether they want this kind on this channel (defaults: push as the kind says; text and email off; others' jobs off). */
export function wantsNotify(prefs: unknown, kind: NotifyKind, channel: NotifyChannel = "push", section: NotifySection = "mine"): boolean {
  const p = normalizeNotifyPrefs(prefs);
  const set = (section === "mine" ? p.mine : p.others)[kind];
  return set?.[channel] ?? (section === "mine" && channel === "push" ? NOTIFY_KIND_INFO[kind].default : false);
}

/** Every kind with every channel filled in (what the settings grid shows). */
export function fullNotifyPrefs(raw: unknown): NotifyPrefs & { mine: Record<NotifyKind, Required<ChannelSet>>; others: Record<NotifyKind, Required<ChannelSet>> } {
  const p = normalizeNotifyPrefs(raw);
  const fill = (section: NotifySection) => Object.fromEntries(NOTIFY_KINDS.map((k) => [k, Object.fromEntries(NOTIFY_CHANNELS.map((c) => [c, wantsNotify(p, k, c, section)]))])) as Record<NotifyKind, Required<ChannelSet>>;
  return { ...p, mine: fill("mine"), others: fill("others") };
}

/** A person's notification settings as the grid edits them, and what they may set. */
export interface NotifySettingsView {
  prefs: ReturnType<typeof fullNotifyPrefs>;
  /** The person may have a "My team's jobs" section (and "everyone's"). */
  canTeam: boolean;
  canEveryone: boolean;
  /** Where texts and emails go (empty: they can't get that channel). */
  phone: string;
  email: string;
  /** Email can be sent from here at all. */
  emailOn: boolean;
  textOn: boolean;
  /** Locked by an admin and you're not one: read only. */
  readOnly: boolean;
}

/** A Google Drive link: Docs, Sheets, Slides, Forms, a folder, or any Drive file. */
export const DRIVE_KINDS = ["document", "spreadsheet", "presentation", "form", "folder", "file"] as const;
export type DriveKind = (typeof DRIVE_KINDS)[number];
export function driveKind(url: string): DriveKind | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (u.protocol !== "https:") return null;
  if (u.hostname === "docs.google.com") {
    const m = /^\/(document|spreadsheets|presentation|forms)\//.exec(u.pathname);
    return m ? ({ document: "document", spreadsheets: "spreadsheet", presentation: "presentation", forms: "form" } as const)[m[1] as "document"] : "file";
  }
  if (u.hostname === "drive.google.com") return u.pathname.includes("/folders/") ? "folder" : "file";
  return null;
}
export const driveLinkInputSchema = z.object({
  title: z.string().trim().min(1, "Give it a name").max(200),
  url: z
    .string()
    .trim()
    .max(2000)
    .refine((u) => driveKind(u) !== null, "Paste a Google Docs, Sheets, or Drive link (https://docs.google.com/… or https://drive.google.com/…)."),
  category: z.string().trim().max(60).default(""),
});
export interface DriveLinkItem {
  id: string;
  title: string;
  url: string;
  kind: DriveKind;
  category: string;
  createdBy: string | null;
  createdAt: string;
}

/** An open job near where someone is standing (the in-app camera's suggestion). */
export interface NearbyJob {
  id: string;
  number: number;
  title: string;
  customerName: string;
  address: string;
  meters: number;
}

// ── People's other calendars (My profile → Calendars) ───────────────────

export type CalendarSharing = "private" | "busy" | "details";
export const CALENDAR_SHARING_LABELS: Record<CalendarSharing, string> = { private: "Just me", busy: "Busy only", details: "Full details" };

/** An event from someone's Google calendar or iCal link, as the viewer may see it. */
export interface ExternalEvent {
  id: string;
  userId: string;
  userName: string;
  /** The calendar's name (only on your own). */
  calendar: string | null;
  color: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  /** "Busy" when shared as busy only. */
  title: string;
  location: string;
  /** Opens it where it lives (your own Google events). */
  link: string;
  mine: boolean;
}

export interface CalendarSourceItem {
  id: string;
  kind: "google" | "ical";
  name: string;
  color: string;
  googleCalendarId: string | null;
  sharing: CalendarSharing;
  fetchedAt: string | null;
  error: string | null;
}

export interface MyCalendars {
  google: {
    available: boolean;
    connected: boolean;
    email: string | null;
    error: string | null;
    /** Connected before reading calendars was asked for: Reconnect to pick and show calendars. */
    needsReconnect: boolean;
    /** Where appointments go; null: not picked yet (their main calendar until they do). */
    target: { id: string; name: string } | null;
    calendars: { id: string; name: string; color: string; primary: boolean; canWrite: boolean }[];
  };
  sources: CalendarSourceItem[];
  /** Your private iCal link of your appointments (null: off). */
  feedUrl: string | null;
}

const sharingSchema = z.enum(["private", "busy", "details"]);
const sourceColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const calendarTargetSchema = z.union([z.object({ calendarId: z.string().min(1).max(300) }), z.object({ create: z.literal(true) })]);
export const calendarSourceAddSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("google"), googleCalendarId: z.string().min(1).max(300), name: z.string().trim().min(1).max(100), color: sourceColor.optional(), sharing: sharingSchema.default("private") }),
  z.object({ kind: z.literal("ical"), url: z.string().trim().min(8).max(2000), name: z.string().trim().min(1).max(100), color: sourceColor.optional(), sharing: sharingSchema.default("private") }),
]);
export const calendarSourceUpdateSchema = z.object({ name: z.string().trim().min(1).max(100).optional(), color: sourceColor.optional(), sharing: sharingSchema.optional() });

// ── Map ───────────────────────────────────────────────────────────────────

/** A job on the Map. Value only for people who see job money. */
export interface MapJob {
  id: string;
  number: number;
  title: string;
  customerName: string;
  address: string;
  workflowId: string;
  workflowName: string;
  stageId: string;
  stageName: string;
  stageKind: StageKind;
  salesRep: UserRef | null;
  /** Other reps' ids (the rep filter matches any of them too). */
  otherRepIds?: string[];
  carrier: string;
  leadSource: string;
  createdAt: string;
  stageChangedAt: string;
  /** When its proposal was signed (or its first invoice, for jobs without one). */
  soldAt: string | null;
  valueCents: number | null;
  lat: number | null;
  lon: number | null;
  /** found: from its address; manual: placed by hand; missing: the address couldn't be found; pending: not looked up yet; none: no address. */
  placed: "found" | "manual" | "missing" | "pending" | "none";
}

export interface MapConfig {
  /** The browser key; null when the Map isn't set up on this server. */
  googleMaps: { apiKey: string; mapId: string } | null;
  views: MapView[];
  canPlace: boolean;
  canSaveViews: boolean;
  /** Where to center when there are no jobs (the company's address). */
  home: { lat: number; lon: number } | null;
}

/** A hail report from NOAA's Storm Prediction Center (spotters, the public, and officials). */
export interface HailReport {
  id: string;
  day: string;
  time: string;
  sizeInches: number;
  place: string;
  county: string;
  state: string;
  lat: number;
  lon: number;
  comments: string;
}

export interface MapAppointment {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  location: string;
  lat: number | null;
  lon: number | null;
  jobId: string | null;
}

export const mapPinSchema = z.union([z.object({ lat: z.number().min(-90).max(90), lon: z.number().min(-180).max(180) }), z.object({ clear: z.literal(true) })]);

// ── Contacts on your phone (CardDAV) ─────────────────────────────────────

export interface ContactsSync {
  /** A password has been made (and not turned off). */
  on: boolean;
  /** What to type into the phone. */
  server: string;
  username: string;
  /** The full address, for apps that want one (DAVx⁵, Thunderbird). */
  url: string;
  lastUsedAt: string | null;
  device: string | null;
  /** Each contact type and whether it goes to the phone. */
  types: { id: string; name: string; on: boolean; count: number }[];
  /** The last requests your phone made (newest first), to see how it gets on. */
  recent: { at: string; method: string; path: string; depth: string | null; kind: string | null; props: string[]; status: number; items: number | null; note: string; agent: string }[];
}

export const contactsSyncTypesSchema = z.object({ typeIds: z.array(z.string().uuid()).max(100).nullable() });

// ── Fred's inbox (email to Fred) ─────────────────────────────────────────

export type InboundEmailStatus = "held" | "queued" | "working" | "done" | "needs_answer" | "couldnt_handle" | "failed" | "dropped" | "ignored";

export interface FredInboxItem {
  id: string;
  receivedAt: string;
  fromAddress: string;
  fromName: string;
  subject: string;
  /** staff, a known contact, an allowed outside sender, or outside (held until someone lets Fred look). */
  trust: "staff" | "contact" | "allowed" | "outside";
  verified: boolean;
  status: InboundEmailStatus;
  summary: string;
  question: string;
  note: string;
  /** Who it's with (the rep), and the job. */
  owner: { id: string; name: string } | null;
  job: { id: string; number: number; customerName: string } | null;
  conversationId: string | null;
  attachments: { name: string; size: number }[];
  /** A preview of the text (for outside emails, so a person can decide). */
  preview: string;
}

export interface FredInbox {
  items: FredInboxItem[];
  allowedSenders: string[];
  /** The address to send to. */
  address: string | null;
  canManage: boolean;
}

export const fredInboxLookSchema = z.object({ userId: z.string().uuid().nullable().default(null), allowSender: z.boolean().default(false) });
export const fredInboxSenderSchema = z.object({ sender: z.string().trim().toLowerCase().regex(/^([^@\s]+@[^@\s]+\.[^@\s]+|@[^@\s]+\.[^@\s]+)$/, "An email address, or @domain.com") });
